import { useMemo, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* ============================================================================
 * MonthStrip — compact horizontal month navigator.
 *   • Desktop: the whole month renders in a single row (grid, equal columns).
 *   • Mobile: horizontal scroll with snap.
 *   • Europe/London dates, Monday-first week separators.
 * Pure presentation — the parent owns data + the selected day's body.
 * ========================================================================== */

const LZ = "Europe/London";
const isoFmt = new Intl.DateTimeFormat("en-CA", { timeZone: LZ, year: "numeric", month: "2-digit", day: "2-digit" });

export function todayLondonIso() { return isoFmt.format(new Date()); }

const WD = ["M", "T", "W", "T", "F", "S", "S"]; // Monday-first single letters

function monthLabel(anchorIso) {
  const [y, m] = anchorIso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}
function daysInMonth(anchorIso) {
  const [y, m] = anchorIso.split("-").map(Number);
  const out = [];
  const d = new Date(Date.UTC(y, m - 1, 1));
  while (d.getUTCMonth() === m - 1) { out.push(d.toISOString().slice(0, 10)); d.setUTCDate(d.getUTCDate() + 1); }
  return out;
}
function weekdayIndexMon(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // Mon=0 … Sun=6
}
function addMonth(anchorIso, n) {
  const [y, m] = anchorIso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10);
}

export default function MonthStrip({
  monthAnchor,     // ISO date within the displayed month
  onMonthChange,   // (firstOfMonthIso) => void
  value,           // selected ISO day
  onChange,        // (iso) => void
  getDayMeta,      // (iso) => { count?, dots?: [hex], special?: bool } | null
  accent = "var(--fl-orange)",
  testid = "month-strip",
}) {
  const today = todayLondonIso();
  const days = useMemo(() => daysInMonth(monthAnchor), [monthAnchor]);

  // Keep the selected day (or today) centred in the scroller — critical on
  // mobile where the whole month can't fit on screen.
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current?.querySelector(
      '[data-selected="true"], [data-today="true"]'
    );
    if (el) el.scrollIntoView({ inline: "center", block: "nearest" });
  }, [value, monthAnchor]);

  return (
    <div
      className="fl-card p-3 md:p-4"
      data-testid={testid}
      style={{ borderColor: `color-mix(in oklab, ${accent} 30%, var(--fl-border))` }}
    >
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => onMonthChange(addMonth(monthAnchor, -1))}
          className="fl-btn-ghost px-2 py-1.5"
          data-testid={`${testid}-prev`}
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-sm font-semibold" style={{ color: "var(--fl-text)" }} data-testid={`${testid}-label`}>
          {monthLabel(monthAnchor)}
        </div>
        <button
          type="button"
          onClick={() => onMonthChange(addMonth(monthAnchor, 1))}
          className="fl-btn-ghost px-2 py-1.5"
          data-testid={`${testid}-next`}
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex md:grid md:grid-flow-col md:auto-cols-fr gap-1 overflow-x-auto md:overflow-visible no-scrollbar pb-1"
        style={{ scrollSnapType: "x proximity" }}
        role="listbox"
        aria-label="Days of the month"
      >
        {days.map((iso) => {
          const meta = (getDayMeta ? getDayMeta(iso) : null) || {};
          const selected = value === iso;
          const isToday = iso === today;
          const wdIdx = weekdayIndexMon(iso);
          const isWeekStart = wdIdx === 0;
          const isWeekend = wdIdx >= 5; // Sat / Sun — distinguished "special" days
          const wd = WD[wdIdx];
          const dots = (meta.dots || []).slice(0, 3);
          return (
            <button
              key={iso}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => onChange && onChange(iso)}
              data-testid={`${testid}-day-${iso}`}
              data-selected={selected ? "true" : "false"}
              data-today={isToday ? "true" : "false"}
              className={`fl-strip-day shrink-0 md:shrink min-w-[44px] md:min-w-0 flex flex-col items-center gap-1 rounded-xl py-2 px-1.5 ${isWeekStart ? "fl-strip-weekstart" : ""}`}
              style={{
                scrollSnapAlign: "center",
                background: selected
                  ? `color-mix(in oklab, ${accent} 20%, transparent)`
                  : (isWeekend ? "color-mix(in oklab, var(--fl-muted-2) 9%, transparent)" : "transparent"),
                border: selected ? `1px solid ${accent}` : "1px solid transparent",
                boxShadow: isToday && !selected ? `inset 0 0 0 1px color-mix(in oklab, ${accent} 50%, transparent)` : "none",
              }}
            >
              <span className="text-[9px] uppercase tracking-wider" style={{ color: selected ? accent : "var(--fl-muted-2)" }}>{wd}</span>
              <span
                className="text-sm font-bold leading-none"
                style={{ color: selected ? accent : (isToday ? "var(--fl-text)" : "var(--fl-text-2)") }}
              >
                {Number(iso.slice(8, 10))}
              </span>
              <span className="h-1.5 flex items-center gap-0.5">
                {meta.count > 0 && dots.length === 0 && (
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                )}
                {dots.map((c, i) => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
